import { useMemo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { CALENDAR_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { minutesToTimeStr } from '../../utils';
import { TooltipContainer } from '../calendar-tooltip/tooltip-container';

type DragCreateSelectionOverlayProps = {
  isEnabled?: boolean;
};

export const DragCreateSelectionOverlay = ({ isEnabled }: DragCreateSelectionOverlayProps) => {
  const { dragCreateSelection, groupIds, groupsById, pxPerMinute } = useCalendarGridStore([
    'dragCreateSelection',
    'groupIds',
    'groupsById',
    'pxPerMinute',
  ]);

  const groupId = dragCreateSelection?.groupId;
  const columnId = dragCreateSelection?.columnId;

  const dragCreateOverlayColumn = useMemo(() => {
    if (!isEnabled || !groupId || !columnId) return undefined;

    const groupIndex = groupIds.indexOf(groupId);
    const groupInfo = groupsById[groupId];
    if (groupIndex < 0 || !groupInfo) return undefined;

    const columnIndex = groupInfo.columnIds.indexOf(columnId);
    if (columnIndex < 0) return undefined;

    // +1 time column, +1 CSS grid 1-based indexing
    const startColumn = groupIndex + groupInfo.groupOffset + 2 + columnIndex;

    return `${startColumn} / span 1`;
  }, [columnId, groupId, groupIds, groupsById, isEnabled]);

  if (!isEnabled || !dragCreateSelection || !dragCreateOverlayColumn) {
    return null;
  }

  const startTime = minutesToTimeStr(dragCreateSelection.startMinutes, 'hh:mm A');
  const endTime = minutesToTimeStr(dragCreateSelection.endMinutes, 'hh:mm A');

  const top = Math.min(dragCreateSelection.startMinutes, dragCreateSelection.endMinutes) * pxPerMinute;
  const height = Math.max(Math.abs(dragCreateSelection.endMinutes - dragCreateSelection.startMinutes) * pxPerMinute, 2);
  return (
    <div aria-hidden='true' css={dragCreateOverlayColumnStyle} style={{ gridColumn: dragCreateOverlayColumn }}>
      <div css={dragCreateSelectionStyle({ top, height })}>
        <TooltipContainer css={tooltipContainerStyle}>
          {dragCreateSelection.startMinutes <= dragCreateSelection.endMinutes
            ? `${startTime} - ${endTime}`
            : `${endTime} - ${startTime}`}
        </TooltipContainer>
      </div>
    </div>
  );
};

const dragCreateOverlayColumnStyle = css({
  gridRow: '2 / -1',
  position: 'relative',
  pointerEvents: 'none',
  zIndex: CALENDAR_Z_INDICES.dragCreateSelectionOverlay,
});

const dragCreateSelectionStyle = ({ top, height }: { top: number; height: number }) =>
  css({
    position: 'absolute',
    top,
    left: 0,
    right: 0,
    height,
    borderRadius: theme.borderRadius.small,
    backgroundColor: theme.colors.primary20,
    border: `1.5px dashed ${theme.colors.primary40}`,
    boxShadow: theme.shadows.light,
    pointerEvents: 'none',
    boxSizing: 'border-box',
  });

const tooltipContainerStyle = css({
  position: 'absolute',
  top: theme.spacing(-5),
  right: '50%',
  transform: 'translateX(50%) !important',
});
